import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiUser, FiMail, FiPhone, FiMessageSquare } from 'react-icons/fi';
import Modal from './Modal';

const honeypotStyle = {
  position: 'absolute',
  left: '-9999px',
  width: 0,
  height: 0,
  opacity: 0,
  overflow: 'hidden',
};

// Map select option values to their human-readable labels for the email body
const serviceTypeLabels = {
  wedding: 'Wedding Flowers',
  corporate: 'Corporate Events',
  birthday: 'Birthday Celebrations',
  anniversary: 'Anniversary',
  other: 'Other Event',
};

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  preferredDate: '',
  preferredTime: '',
  serviceType: '',
  message: '',
  website: '', // honeypot — must stay empty
};

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    // Compose message: consultation details + optional user message
    const parts = ['Consultation booking request'];
    if (formData.preferredDate) parts.push(`Preferred date: ${formData.preferredDate}`);
    if (formData.preferredTime) parts.push(`Preferred time: ${formData.preferredTime}`);
    if (formData.serviceType) parts.push(`Service interest: ${serviceTypeLabels[formData.serviceType] || formData.serviceType}`);
    if (formData.message.trim()) parts.push(formData.message.trim());
    const composedMessage = parts.join('\n\n');

    try {
      const response = await fetch('/api/enquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: composedMessage,
          source: 'consultation-modal',
          website: formData.website,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData(initialFormData);
          onClose();
        }, 5000);
      } else {
        setSubmitError(data.error || 'Something went wrong. Please try again or call 0466 645 060.');
      }
    } catch (err) {
      setSubmitError('Network error. Please check your connection and try again, or call 0466 645 060.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM'
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="95%" title="Book Free Consultation">
      <div className="p-6 max-h-[80vh] overflow-y-auto">
        {submitSuccess ? (
          <div className="py-12 text-center">
            <h3 className="text-2xl font-bold text-neutral-800 mb-3">Thank you!</h3>
            <p className="text-neutral-600 mb-2">
              We've received your consultation request and will be in touch within 24–48 hours to confirm.
            </p>
            <p className="text-neutral-600">
              If your enquiry is urgent, please call us on <strong>0466 645 060</strong>.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-neutral-600">
                Schedule a free consultation instore at our showroom with our floral design experts. We'll discuss your vision,
                show you our collection, and create a customized proposal for your event.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot — must remain empty */}
              <div style={honeypotStyle} aria-hidden="true">
                <label htmlFor="website-cm">Website (leave blank)</label>
                <input
                  type="text"
                  id="website-cm"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{submitError}</p>
                </div>
              )}

              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                  <FiUser className="text-primary-500" />
                  Personal Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0466 645 060"
                  />
                </div>
              </div>

              {/* Consultation Details */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                  <FiCalendar className="text-primary-500" />
                  Consultation Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Preferred Time *
                    </label>
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select time</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Service Interest
                  </label>
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select service type</option>
                    <option value="wedding">Wedding Flowers</option>
                    <option value="corporate">Corporate Events</option>
                    <option value="birthday">Birthday Celebrations</option>
                    <option value="anniversary">Anniversary</option>
                    <option value="other">Other Event</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                  <FiMessageSquare className="text-primary-500" />
                  Additional Information
                </h3>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    placeholder="Tell us about your event, color preferences, or any specific requirements..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="flex-1 btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 btn bg-accent-purple-500 hover:bg-accent-purple-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Book Consultation'}
                </button>
              </div>
            </form>

            {/* Additional Info */}
            <div className="mt-8 p-4 bg-primary-50 rounded-lg">
              <h4 className="font-semibold text-neutral-800 mb-2">What to Expect</h4>
              <ul className="text-sm text-neutral-600 space-y-1">
                <li>• 30-minute free consultation with our design experts</li>
                <li>• Discussion of your event vision and requirements</li>
                <li>• Review of our premium faux flower collection</li>
                <li>• Customized proposal with pricing and options</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ContactModal;
